/**
 * The assistant's catalog wording for served codes (TMMAPP-0142 U1): the data categories, the
 * privacy panel's fixed statements, a failed turn's reason and the scope kinds. A code this build
 * doesn't know reads a generic sentence — never the raw code (TXT-006).
 *
 * A category reads the registry's served `labelKey` / `descriptionKey` when the catalog has it
 * (TMMAPP-0142 H11), so a renamed key keeps its words; the key-based copy is the fallback, and only
 * a key neither knows reads "Other data". Lists go through `formatList` (H9).
 */
import type { TFunction } from 'i18next';
import type {
  AssistantAskReason,
  AssistantCategoryOption,
  AssistantPrivateData,
  AssistantScopeKind,
  AssistantSession,
} from '@/api/assistant';
import { formatList } from '@/i18n/format';
import { personName } from '@/i18n/personName';

/** The registry at the pin (TMMAPI-0181 U1 item 4); a new key reads "Other data" until labelled. */
const CATEGORY_KEYS: ReadonlySet<string> = new Set([
  'TRANSACTIONS',
  'BALANCES',
  'VENDOR_NAMES',
  'PEOPLE_NAMES',
  'MEMOS',
  'PLANNED',
  'BUDGETS',
  'LOAN_AND_CARD_TERMS',
  'HOLDINGS',
  'NET_WORTH',
  'MEETING_NOTES',
  'CONTRIBUTIONS',
]);

/** The served catalog key's text when the catalog has it (only the category family is read). */
function servedCopy(t: TFunction, servedKey: string | undefined): string | null {
  if (servedKey === undefined || !servedKey.startsWith('assistant.category.')) return null;
  const text = t(servedKey, { defaultValue: '' });
  return text.length > 0 && text !== servedKey ? text : null;
}

/** The registry entry for a key, to read its served label and description keys. */
export type CategoryRegistry = readonly AssistantCategoryOption[] | null | undefined;

function entryOf(registry: CategoryRegistry, key: string): AssistantCategoryOption | undefined {
  return registry?.find((option) => option.key === key);
}

export function categoryLabel(t: TFunction, key: string, registry?: CategoryRegistry): string {
  const served = servedCopy(t, entryOf(registry, key)?.labelKey);
  if (served !== null) return served;
  return CATEGORY_KEYS.has(key)
    ? t(`assistant.category.${key.toLowerCase()}.label`)
    : t('assistant.categoryUnknown');
}

export function categoryDescription(
  t: TFunction,
  key: string,
  registry?: CategoryRegistry,
): string | null {
  const served = servedCopy(t, entryOf(registry, key)?.descriptionKey);
  if (served !== null) return served;
  return CATEGORY_KEYS.has(key) ? t(`assistant.category.${key.toLowerCase()}.description`) : null;
}

/** The categories' labels as one list ("Memos, Balances and Holdings"). */
export function categoryList(
  t: TFunction,
  keys: readonly string[],
  locale: string,
  registry?: CategoryRegistry,
): string {
  return formatList(
    keys.map((key) => categoryLabel(t, key, registry)),
    locale,
  );
}

/** A member's private data as its banner names it: "Checking ••1234", or the category. */
export function privateDataItem(
  t: TFunction,
  item: AssistantPrivateData,
  registry?: CategoryRegistry,
): string {
  if (item.kind === 'CATEGORY') return categoryLabel(t, item.category ?? '', registry);
  const name = item.accountName ?? t('assistant.shared.anAccount');
  return item.last4 !== null && item.last4.length > 0
    ? t('assistant.shared.accountMasked', { name, last4: item.last4 })
    : name;
}

/** The banner line: "This session includes Alex's Checking ••1234" (served masked text). */
export function privateDataBanner(
  t: TFunction,
  item: AssistantPrivateData,
  registry?: CategoryRegistry,
): string {
  return t(item.mine ? 'assistant.shared.bannerMine' : 'assistant.shared.banner', {
    member: personName(item.memberName, t),
    item: privateDataItem(t, item, registry),
  });
}

const STATEMENTS: ReadonlySet<string> = new Set([
  'ACCOUNT_NUMBERS',
  'CARD_NUMBERS',
  'EMAILS',
  'OUT_OF_SCOPE_DATA',
  'CATEGORIES_OFF',
]);

/** A fixed "never sent" statement; null for a code this build doesn't know (it is not shown). */
export function statementLabel(t: TFunction, code: string): string | null {
  return STATEMENTS.has(code) ? t(`assistant.privacy.statement.${code}`) : null;
}

const ALSO_SENT: ReadonlySet<string> = new Set(['MEMBER_FIRST_NAMES']);

/**
 * What else the calls send, beside the data on (`inScope.alsoSent`, TMMAPI-0206); null for a code
 * this build doesn't know (it is not shown).
 */
export function alsoSentLabel(t: TFunction, code: string): string | null {
  return ALSO_SENT.has(code) ? t(`assistant.privacy.alsoSent.${code}`) : null;
}

/** "Checking and Household", or "Checking, Household and 2 more"; unavailable ones are left out. */
export function scopeSummary(t: TFunction, session: AssistantSession, locale: string): string {
  const names = session.scope.flatMap((item) =>
    item.available && item.name !== null ? [item.name] : [],
  );
  if (names.length === 0) return t('assistant.scopeSummaryEmpty');
  if (names.length <= 2) return formatList(names, locale);
  // "Checking, Household and 2 more": the shown names and the rest as one list (H9).
  const more = t('assistant.scopeSummaryMore', { count: names.length - 2 });
  return formatList([...names.slice(0, 2), more], locale);
}

export function scopeKindLabel(t: TFunction, kind: AssistantScopeKind): string {
  return t(`assistant.scopeKind.${kind}`);
}

/**
 * A failed turn's own reasons (TMMAPI-0181 U2 item 13–14); the AI gate's reasons (`WALLET_EMPTY`,
 * `GLOBAL`, …) are read by `TurnFailure` through the existing AI copy.
 */
const TURN_REASONS: ReadonlySet<string> = new Set([
  'INTERRUPTED',
  'SUPERSEDED',
  'LOOP_LIMIT',
  'PROVIDER_ERROR',
  'SESSION_ARCHIVED',
  'SCOPE_NARROWED',
  'ACCESS_ENDED',
  // TMMAPI-0181 U7: the question's own files can't fit the call; nothing was charged.
  'ATTACHMENT_TOO_LARGE',
  // TMMAPI-0182 U1 (J7): the question and its files are too large to read at once; no number shown.
  'CONTEXT_WINDOW',
]);

/** The turn's own reason's sentence, or null when the reason is the AI gate's (or unknown). */
export function turnReasonCopy(t: TFunction, reason: string): string | null {
  return TURN_REASONS.has(reason) ? t(`assistant.failure.${reason}`) : null;
}

/**
 * Who pays for an anchored session (TMMAPP-0148 U1 item 7; TMMAPI-0184 U3 `paidBy`): "Paid from
 * your AI credit", or the owner's; null when the person pays for it themselves (no `paidBy`).
 */
export function paidByCopy(t: TFunction, session: AssistantSession): string | null {
  const payer = session.paidBy;
  if (payer === null) return null;
  if (payer.mine) return t('assistant.payer.mine');
  const name = payer.name?.trim() ?? '';
  return name.length > 0
    ? t('assistant.payer.theirs', { name })
    : t('assistant.payer.theirsUnnamed');
}

/**
 * The member's own monthly allowance is used up (`MEMBER_LIMIT`, TMMAPI-0186 U1; TMMAPP-0153 item
 * 17): "You've reached your monthly allowance for the assistant on {virtual account}. {owner} can
 * change it." No figure and no wallet prompt; each name is left out when it isn't known.
 */
export function memberLimitCopy(
  t: TFunction,
  virtualAccountName: string | null,
  ownerName: string | null,
): string {
  const virtualAccount = virtualAccountName?.trim() ?? '';
  const owner = ownerName?.trim() ?? '';
  if (virtualAccount.length > 0) {
    return owner.length > 0
      ? t('assistant.memberLimit.named', { virtualAccount, owner })
      : t('assistant.memberLimit.noOwner', { virtualAccount });
  }
  return owner.length > 0
    ? t('assistant.memberLimit.noVirtualAccount', { owner })
    : t('assistant.memberLimit.generic');
}

/**
 * Why the caller can't ask the assistant in a shared session (TMMAPP-0164 item 14, TMMAPI-0195 U4),
 * by `canAskReason` (or a refused ask's served reason). `LIMIT_REACHED` is the member limit's own
 * sentence (`memberLimitCopy`, the copy `409 AI_DISABLED {MEMBER_LIMIT}` reads); the others say the
 * members can still be messaged. Never a wallet figure.
 */
export function cantAskCopy(
  t: TFunction,
  reason: AssistantAskReason | null,
  virtualAccountName: string | null,
  ownerName: string | null,
): string {
  const virtualAccount = virtualAccountName?.trim() ?? '';
  const owner = ownerName?.trim() ?? '';
  if (reason === 'LIMIT_REACHED') return memberLimitCopy(t, virtualAccountName, ownerName);
  if (reason === 'AI_OFF') return t('assistant.cantAsk.aiOff');
  if (reason === 'NOT_ALLOWED' && virtualAccount.length > 0) {
    return owner.length > 0
      ? t('assistant.cantAsk.notAllowed', { owner, virtualAccount })
      : t('assistant.cantAsk.notAllowedNoOwner', { virtualAccount });
  }
  return t('assistant.cantAsk.generic');
}

/**
 * The payer's refusal (`PAYER_UNAVAILABLE`), naming them when served; never a figure (item 8). Its
 * reason picks one sentence (TMMAPI-0185 U1, TMMAPP-0149 item 4): `MONTHLY_LIMIT`, `BUSY` and
 * `UNAVAILABLE` (our side) have their own; every other reason, and an unknown one, reads item 8's.
 */
export function payerUnavailableCopy(
  t: TFunction,
  payerName: string | null,
  reason: string | null = null,
): string {
  if (reason === 'BUSY') return t('assistant.payer.busy');
  const name = payerName?.trim() ?? '';
  const named = name.length > 0;
  if (reason === 'MONTHLY_LIMIT') {
    return named
      ? t('assistant.payer.monthlyLimit', { name })
      : t('assistant.payer.monthlyLimitGeneric');
  }
  if (reason === 'UNAVAILABLE') {
    return named ? t('assistant.payer.ourSide', { name }) : t('assistant.payer.ourSideGeneric');
  }
  return named
    ? t('assistant.payer.unavailable', { name })
    : t('assistant.payer.unavailableGeneric');
}
